/**
 * Dates as the person in front of the screen counts them.
 *
 * <p>`new Date().toISOString().slice(0, 10)` — the idiom this replaces, and the one most of
 * this codebase still uses — is the local date only west of UTC. In Dubai it is yesterday's
 * date every day between midnight and 4am, so somebody marking themselves out of office at
 * 1am gets a range that ended before it started, and the server, which computes "today" in the
 * server's own zone, disagrees with the form the person just filled in.
 *
 * <p>Found by a Playwright test that ran at 01:52 local and saw a range of "05 Sep – 05 Sep"
 * not count as covering the 6th. The rest of the codebase has the same idiom in about a dozen
 * places; they are worth converting, but not silently as part of another change.
 */

/** Today, in the browser's own zone, as `YYYY-MM-DD`. */
export function todayLocal(): string {
  return toIsoDate(new Date())
}

/** `date` plus `days`, in the browser's own zone, as `YYYY-MM-DD`. */
export function isoDatePlusDays(days: number, from: Date = new Date()): string {
  const shifted = new Date(from)
  shifted.setDate(shifted.getDate() + days)
  return toIsoDate(shifted)
}

function toIsoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

/** A `Date` as `YYYY-MM-DD` in the browser's own zone. */
export function isoDate(date: Date): string {
  return toIsoDate(date)
}

/** The first day of the month `date` falls in, as `YYYY-MM-DD`. */
export function firstOfMonth(date: Date = new Date()): string {
  return toIsoDate(new Date(date.getFullYear(), date.getMonth(), 1))
}

/** The last day of the month `date` falls in, as `YYYY-MM-DD`. */
export function lastOfMonth(date: Date = new Date()): string {
  return toIsoDate(new Date(date.getFullYear(), date.getMonth() + 1, 0))
}
